import React, { useEffect, useState } from 'react';
import { Shield, AlertTriangle, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';

interface Blocker {
    type: string;
    severity: string;
    message: string;
    current?: number;
    required?: number;
    resolution: string;
}

interface QualityGateProps {
    entityId: string;
    projectId: string;
    mode?: 'RECO' | 'STRICT';
    onValidate?: () => void;
}

const QualityGate: React.FC<QualityGateProps> = ({
    entityId,
    projectId,
    mode = 'RECO',
    onValidate
}) => {
    const [status, setStatus] = useState<string | null>(null);
    const [blockers, setBlockers] = useState<Blocker[]>([]);
    const [isValidating, setIsValidating] = useState(false);

    useEffect(() => {
        validateQualityGate();
    }, [entityId, projectId, mode]);

    const validateQualityGate = async () => {
        try {
            setIsValidating(true);
            const response = await fetch(
                `/api/spec-review/projects/${projectId}/entities/${entityId}/validate?mode=${mode}`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            if (response.ok) {
                const data = await response.json();
                setStatus(data.status);
                setBlockers(data.blockers);
                onValidate?.();
            }
        } catch (error) {
            console.error('Error validating quality gate:', error);
        } finally {
            setIsValidating(false);
        }
    };

    const getStatusBadge = () => {
        if (!status) return null;

        const isOk = status.endsWith('_OK');
        const bgColor = isOk ? 'bg-green-500/20' : 'bg-red-500/20';
        const borderColor = isOk ? 'border-green-500/50' : 'border-red-500/50';
        const textColor = isOk ? 'text-green-400' : 'text-red-400';
        const Icon = isOk ? CheckCircle2 : XCircle;

        return (
            <div className={`flex items-center gap-2 px-3 py-2 ${bgColor} border ${borderColor} rounded-lg`}>
                <Icon className={`w-5 h-5 ${textColor}`} />
                <span className={`font-semibold ${textColor}`}>
                    {status.replace('_', ' ')}
                </span>
            </div>
        );
    };

    const getSeverityColor = (severity: string) => {
        switch (severity) {
            case 'critical':
                return 'text-red-400 bg-red-500/10 border-red-500/20';
            case 'high':
                return 'text-orange-400 bg-orange-500/10 border-orange-500/20';
            case 'medium':
                return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20';
            case 'low':
                return 'text-blue-400 bg-blue-500/10 border-blue-500/20';
            default:
                return 'text-slate-400 bg-slate-500/10 border-slate-500/20';
        }
    };

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" />
                    <h4 className="text-md font-semibold text-white">
                        Quality Gate ({mode} Mode)
                    </h4>
                </div>
                <div className="flex items-center gap-2">
                    {getStatusBadge()}
                    <button
                        onClick={validateQualityGate}
                        disabled={isValidating}
                        className="p-2 hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
                        title="Refresh validation"
                    >
                        <RefreshCw className={`w-4 h-4 text-slate-400 ${isValidating ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* Blockers */}
            {blockers.length > 0 && (
                <div className="space-y-3">
                    <p className="text-sm text-slate-400">
                        {blockers.length} issue{blockers.length !== 1 ? 's' : ''} found:
                    </p>
                    {blockers.map((blocker, idx) => (
                        <div
                            key={idx}
                            className={`border rounded-lg p-4 ${getSeverityColor(blocker.severity)}`}
                        >
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <p className="font-semibold mb-1">{blocker.message}</p>
                                    <p className="text-sm opacity-90 mb-2">
                                        <span className="font-medium">Resolution:</span> {blocker.resolution}
                                    </p>
                                    {blocker.current !== undefined && blocker.required !== undefined && (
                                        <div className="text-xs opacity-75">
                                            Current: {blocker.current} | Required: {blocker.required}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Success message */}
            {blockers.length === 0 && status && (
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                    <p className="text-green-400 flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5" />
                        All quality checks passed! Ready to generate test cases.
                    </p>
                </div>
            )}
        </div>
    );
};

export default QualityGate;
